import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useDisplay } from "@/context/DisplayContext";
import { Sparkles, Trophy, Target, Loader2, ChevronRight, TrendingUp, CheckCircle2, AlertTriangle, Lightbulb } from "lucide-react";

export default function SmartPicks({ insights, insightsLoading }) {
  const navigate = useNavigate();
  const { fmtProb } = useDisplay();
  const [smart, setSmart] = useState(null);
  const [picks, setPicks] = useState(null); // null = not loaded, [] = none
  const [picksLoading, setPicksLoading] = useState(false);
  const [picksFallback, setPicksFallback] = useState(false);

  useEffect(() => {
    api.get("/dashboard/smart").then((r) => setSmart(r.data)).catch(() => setSmart({ settled_bets: 0 }));
  }, []);

  useEffect(() => {
    if (!smart || !(smart.suggest_competitions || []).length) return;
    setPicksLoading(true);
    api.get("/best-bets")
      .then((r) => {
        const comps = new Set(smart.suggest_competitions);
        const all = r.data.picks || [];
        let matched = all.filter((p) => comps.has(p.tournament));
        const fellBack = matched.length === 0;
        if (fellBack) matched = all; // fallback: still show strong picks
        setPicksFallback(fellBack);
        setPicks(matched.slice(0, 3));
      })
      .catch(() => setPicks([]))
      .finally(() => setPicksLoading(false));
  }, [smart]);

  if (!smart) return null;

  const hasRankings = (smart.top_competitions || []).length || (smart.top_markets || []).length;
  const hasInsights = insights && insights.headline;
  if (!hasRankings && !hasInsights && !insightsLoading) return null;

  return (
    <div className="glass rounded-3xl p-5 sm:p-6 space-y-5" data-testid="smart-picks">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
        <h3 className="font-display font-bold text-lg">Made for you</h3>
        {insightsLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* Plain-English AI briefing */}
      {hasInsights && (
        <div className="space-y-4" data-testid="smart-briefing">
          <p className="font-medium text-base" data-testid="ai-headline">{insights.headline}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center gap-1.5 text-[#34C759] text-sm font-semibold mb-2">
                <CheckCircle2 className="h-4 w-4" /> What's working
              </div>
              <ul className="space-y-1.5">
                {(insights.working || []).length ? insights.working.map((w, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-[#34C759] mt-0.5">•</span><span>{w}</span>
                  </li>
                )) : <li className="text-sm text-muted-foreground">Not much yet.</li>}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-[#FF3B30] text-sm font-semibold mb-2">
                <AlertTriangle className="h-4 w-4" /> Losing money
              </div>
              <ul className="space-y-1.5">
                {(insights.leaking || []).length ? insights.leaking.map((w, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-[#FF3B30] mt-0.5">•</span><span>{w}</span>
                  </li>
                )) : <li className="text-sm text-muted-foreground">Nothing bleeding, nice.</li>}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-primary text-sm font-semibold mb-2">
                <Lightbulb className="h-4 w-4" /> Simple tips
              </div>
              <ul className="space-y-1.5">
                {(insights.suggestions || []).map((w, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-primary mt-0.5">•</span><span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {hasRankings ? (
      <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-border pt-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5" /> Your best competitions
          </div>
          <div className="space-y-1.5" data-testid="smart-top-competitions">
            {(smart.top_competitions || []).map((c) => (
              <button
                key={c.name}
                onClick={() => navigate(`/tournaments/${encodeURIComponent(c.name)}`)}
                className="w-full flex items-center justify-between text-sm rounded-xl bg-secondary/40 px-3 py-2 hover:bg-secondary/70 transition-colors"
              >
                <span className="font-medium truncate">{c.name}</span>
                <span className="shrink-0 font-semibold" style={{ color: c.profit >= 0 ? "#34C759" : "#FF375F" }}>
                  {c.roi >= 0 ? "+" : ""}{c.roi}%
                </span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5" /> Your best markets
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="smart-top-markets">
            {(smart.top_markets || []).map((m) => (
              <span
                key={m.name}
                className="text-xs rounded-full px-2.5 py-1 font-medium"
                style={{ backgroundColor: (m.profit >= 0 ? "#34C759" : "#FF375F") + "1a", color: m.profit >= 0 ? "#34C759" : "#FF375F" }}
              >
                {m.name} · {m.roi >= 0 ? "+" : ""}{m.roi}%
              </span>
            ))}
          </div>
        </div>
      </div>

      {((smart.suggest_competitions || []).length > 0) && (
        <div className="border-t border-border pt-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" /> {picksFallback ? "Top picks right now" : "Suggested picks for you"}
          </div>
          {picksLoading ? (
            <div className="h-16 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : picks && picks.length > 0 ? (
            <div className="space-y-2" data-testid="smart-suggested-picks">
              {picks.map((p, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/tournaments/${encodeURIComponent(p.tournament)}`)}
                  data-testid="smart-pick"
                  className="w-full flex items-center gap-3 rounded-2xl bg-secondary/40 p-3 hover:bg-secondary/70 transition-colors text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{p.player} · {p.prop}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.tournament} · {p.team} v {p.opponent}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display font-bold text-primary">{fmtProb(p.recent?.pct)}</div>
                    {p.odd != null && <div className="text-[11px] text-muted-foreground">@{Number(p.odd).toFixed(2)}</div>}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No live picks in your best competitions right now.</p>
          )}
        </div>
      )}
      </>
      ) : null}
    </div>
  );
}
